import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, Phone, MessageSquare, Navigation, ScanLine, CheckCircle2, Loader2,
  Settings, X, LogOut, Package, Camera, Clock, Key, Route, AlertTriangle, MessageCircle, Gauge,
} from "lucide-react";
import { driverApi } from "../lib/api";
import PwaInstallPrompt from "../components/PwaInstallPrompt";
import DeliverProofModal from "../components/DeliverProofModal";
import DriverQRScannerModal from "../components/DriverQRScannerModal";
import DriverPushToggle from "../components/DriverPushToggle";
import DriverProblemReportModal from "../components/DriverProblemReportModal";
import DriverChatModal from "../components/DriverChatModal";
import DriverKmModal from "../components/DriverKmModal";
import { useGeolocationWatch } from "../hooks/useGeolocationWatch";

const DELIVERY_LABEL = {
  same_day: "Same-Day", express: "Express", evening: "Aften", business: "Erhverv",
};

const TABS = [
  { id: "in_progress", label: "I gang" },
  { id: "all", label: "Alle" },
  { id: "completed", label: "Afsluttet" },
];

export default function DriverDashboardPage() {
  const [bookings, setBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [updatingId, setUpdatingId] = React.useState(null);
  const [deliverFor, setDeliverFor] = React.useState(null);
  const [scannerOpen, setScannerOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [tab, setTab] = React.useState("in_progress");
  const [optimizing, setOptimizing] = React.useState(false);
  const [pendingTrainings, setPendingTrainings] = React.useState([]);
  const [activeTraining, setActiveTraining] = React.useState(null);
  // Fase 1C — Chauffør drift
  const [problemFor, setProblemFor] = React.useState(null); // booking | true (no booking)
  const [chatOpen, setChatOpen] = React.useState(false);
  const [kmFor, setKmFor] = React.useState(null);  // {booking, mode}
  const [chatUnread, setChatUnread] = React.useState(0);
  const [pauseStatus, setPauseStatus] = React.useState(null);
  const nav = useNavigate();
  const token = localStorage.getItem("ema_driver_token");
  const name = localStorage.getItem("ema_driver_name") || "Chauffør";

  const loadTrainings = React.useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await driverApi().get("/driver/trainings/pending");
      setPendingTrainings(data || []);
      // Auto-open first pending training if any (lockout enforcement)
      if (data && data.length > 0 && !activeTraining) {
        setActiveTraining(data[0]);
      }
    } catch (e) { console.warn("Load trainings failed:", e?.response?.status || e?.message); }
  }, [token, activeTraining]);

  React.useEffect(() => { loadTrainings(); }, [loadTrainings]);

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await driverApi().get("/driver/today");
      setBookings(data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("ema_driver_token"); nav("/driver/login"); }
      else if (e?.response?.status === 403 && /onboarding/i.test(e?.response?.data?.detail || "")) {
        nav("/driver/onboarding");
      }
      else toast.error("Kunne ikke hente leveringer");
    } finally { setLoading(false); }
  }, [token, nav]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Fase 1C — poll unread chat + pause status
  const loadDriftStatus = React.useCallback(async () => {
    if (!token) return;
    try {
      const [msgR, statusR] = await Promise.all([
        driverApi().get("/driver/messages"),
        driverApi().get("/driver/me/status"),
      ]);
      setChatUnread(msgR.data?.unread_count || 0);
      setPauseStatus(statusR.data);
    } catch { /* silent */ }
  }, [token]);
  React.useEffect(() => { loadDriftStatus(); }, [loadDriftStatus]);
  React.useEffect(() => {
    const id = setInterval(loadDriftStatus, 30000);
    return () => clearInterval(id);
  }, [loadDriftStatus]);

  // GPS — always on, silently
  const activeBooking = bookings.find((b) => b.booking_status === "in_transit");
  const uploader = React.useCallback(async (payload) => {
    try { await driverApi().post("/driver/location", { ...payload, booking_id: activeBooking?.id }); }
    catch (e) { console.error("GPS upload failed:", e); }
  }, [activeBooking?.id]);
  const { position, error: gpsError } = useGeolocationWatch({ active: true, uploader, intervalMs: 15000 });

  if (!token) return <Navigate to="/driver/login" replace />;

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await driverApi().patch(`/driver/bookings/${id}/status`, { booking_status: status });
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, booking_status: status } : b));
      load();
    } catch { toast.error("Kunne ikke opdatere"); }
    finally { setUpdatingId(null); }
  };

  const logCall = async (bookingId) => {
    const outcome = prompt("Hvad skete der? (no_answer / reached / voicemail / wrong_number)", "no_answer");
    if (!outcome) return;
    if (!["no_answer", "reached", "voicemail", "wrong_number"].includes(outcome)) {
      toast.error("Ugyldig værdi. Brug: no_answer, reached, voicemail eller wrong_number");
      return;
    }
    try {
      await driverApi().post("/driver/bookings/call-attempt", { booking_id: bookingId, outcome });
      toast.success("Opkald registreret");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke logge opkald");
    }
  };

  const failDelivery = async (bookingId) => {
    const reason = prompt("Hvorfor ikke leveret? (recipient_not_home / wrong_address / refused / other)", "recipient_not_home");
    if (!reason) return;
    if (!["recipient_not_home", "wrong_address", "refused", "other"].includes(reason)) {
      toast.error("Ugyldig årsag");
      return;
    }
    const note = prompt("Note (valgfri):") || "";
    try {
      await driverApi().post("/driver/bookings/fail-delivery", {
        booking_id: bookingId, reason, note,
      });
      toast.success("Forgæves registreret. Admin er underrettet.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke markere forgæves");
    }
  };

  const logout = () => {
    localStorage.removeItem("ema_driver_token");
    localStorage.removeItem("ema_driver_name");
    nav("/driver/login");
  };

  const optimizeRoute = async () => {
    if (!position) {
      toast.error("Venter på GPS — prøv igen om et øjeblik");
      return;
    }
    setOptimizing(true);
    try {
      const { data } = await driverApi().post("/driver/route/optimize", {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      toast.success(data.message || "Rute optimeret");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke optimere rute");
    } finally {
      setOptimizing(false);
    }
  };

  // Filter & order
  const inProgress = bookings.filter((b) => b.booking_status === "confirmed" || b.booking_status === "in_transit");
  const completed = bookings.filter((b) => b.booking_status === "delivered");
  const counts = { in_progress: inProgress.length, all: bookings.length, completed: completed.length };
  const visible = tab === "in_progress" ? inProgress : tab === "completed" ? completed : bookings;

  const hasInProgress = inProgress.length > 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="driver-dashboard">
      {/* === Slim header === */}
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${position ? "bg-emerald-400 animate-pulse" : gpsError ? "bg-amber-400" : "bg-white/30"}`}
              title={gpsError || (position ? "GPS aktiv" : "Henter GPS...")}
            />
            <div className="leading-tight min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold">EMA Kurer</div>
              <div className="font-display font-semibold text-sm truncate" data-testid="driver-name">{name}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={load} data-testid="driver-refresh" className="w-10 h-10 flex items-center justify-center hover:bg-white/5" aria-label="Opdater">
              <RefreshCw size={17} className={loading ? "animate-spin text-brand-red" : "text-white/70"} />
            </button>
            <button onClick={() => setSettingsOpen(true)} data-testid="driver-settings-btn" className="w-10 h-10 flex items-center justify-center hover:bg-white/5" aria-label="Indstillinger">
              <Settings size={17} className="text-white/70" />
            </button>
          </div>
        </div>
      </header>

      {/* === Tabs === */}
      <nav className="max-w-2xl mx-auto px-4 pt-5 pb-2" data-testid="driver-tabs">
        <div className="bg-white/[0.04] border border-white/[0.08] p-1 flex gap-1">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex-1 text-xs font-bold uppercase tracking-[0.18em] py-2.5 transition-colors ${
                  active ? "bg-white text-[#0A0A0A]" : "text-brand-red hover:text-white"
                }`}
              >
                {t.label} <span className={active ? "text-[#0A0A0A]/60" : "text-white/40"}>({counts[t.id]})</span>
              </button>
            );
          })}
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-4 pb-32 space-y-5">
        {/* Pause banner — Fase 1C */}
        {pauseStatus?.is_paused && (
          <div className="bg-red-900/40 border border-red-500/50 p-4 flex items-start gap-3" data-testid="driver-pause-banner">
            <AlertTriangle size={20} className="text-red-300 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-bold text-red-200 mb-1">Du er midlertidigt sat på pause</div>
              <div className="text-red-100/80 text-xs">
                Årsag: <b>{pauseStatus.paused_reason || "ukendt"}</b>. Drift kontakter dig snarest — du kan ikke tage nye opgaver indtil de genåbner.
              </div>
            </div>
          </div>
        )}

        {/* Drift actions — Fase 1C */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setProblemFor(true)}
            data-testid="open-problem-report"
            className="bg-white/[0.04] border border-white/[0.1] hover:border-red-400 hover:bg-red-900/20 text-white py-3 px-3 flex items-center justify-center gap-2 transition-colors"
          >
            <AlertTriangle size={14} className="text-red-300" />
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase">Rapportér problem</span>
          </button>
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            data-testid="open-driver-chat"
            className="relative bg-white/[0.04] border border-white/[0.1] hover:border-brand-red hover:bg-brand-red/10 text-white py-3 px-3 flex items-center justify-center gap-2 transition-colors"
          >
            <MessageCircle size={14} className="text-brand-red" />
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase">Drift-chat</span>
            {chatUnread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-brand-red text-white text-[10px] font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1" data-testid="chat-unread-badge">
                {chatUnread}
              </span>
            )}
          </button>
        </div>

        {/* Sprint 2L — B2B Driver PWA — Mine ruter */}
        <a
          href="/driver/my-routes"
          data-testid="open-my-routes"
          className="block w-full bg-brand-red hover:bg-brand-red/90 text-white py-3.5 px-4 text-center transition-colors"
        >
          <span className="text-xs font-bold tracking-[0.18em] uppercase">Mine B2B-ruter (Sprint 2L)</span>
        </a>

        {/* Sprint 2R-3B — Opfølgningssager (driver-facing follow-up + conversation booking) */}
        <a
          href="/driver/followup"
          data-testid="open-driver-followup"
          className="block w-full bg-white/[0.04] border border-white/[0.15] hover:border-brand-red hover:bg-brand-red/10 text-white py-2.5 px-4 text-center transition-colors"
        >
          <span className="text-[11px] font-bold tracking-[0.18em] uppercase">Opfølgningssager</span>
        </a>

        {/* Route optimize button — only on in_progress tab with 2+ stops */}
        {tab === "in_progress" && inProgress.length >= 2 && (
          <button
            type="button"
            onClick={optimizeRoute}
            disabled={optimizing || !position}
            data-testid="optimize-route-btn"
            className="w-full bg-white/[0.04] border border-white/[0.1] hover:border-brand-red hover:bg-brand-red/10 text-white py-3.5 px-4 flex items-center justify-center gap-2.5 transition-colors disabled:opacity-50"
          >
            {optimizing ? (
              <Loader2 size={16} className="animate-spin text-brand-red" />
            ) : (
              <Route size={16} className="text-brand-red" />
            )}
            <span className="text-xs font-bold tracking-[0.18em] uppercase">
              {optimizing ? "Optimerer rute..." : !position ? "Henter GPS..." : `Optimér rute (${inProgress.length} stops)`}
            </span>
          </button>
        )}

        {loading && bookings.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-white/40" />
          </div>
        )}

        {!loading && visible.length === 0 && (
          <EmptyState tab={tab} />
        )}

        {/* Stops list */}
        {visible.map((b, i) => {
          const isActive = b.booking_status === "confirmed" || b.booking_status === "in_transit";
          const isInTransit = b.booking_status === "in_transit";
          const stopNumber = i + 1;
          // Highlight: first in-progress stop is the "active" one (pink button), others muted
          const isActionable = isActive && (b.id === inProgress[0]?.id || !hasInProgress);
          return (
            <StopCard
              key={b.id}
              stopNumber={stopNumber}
              booking={b}
              isActive={isActive}
              isInTransit={isInTransit}
              isActionable={isActionable}
              isUpdating={updatingId === b.id}
              onScanQR={() => setScannerOpen(true)}
              onMarkPickedUp={() => updateStatus(b.id, "in_transit")}
              onCompleteDelivery={() => setDeliverFor(b)}
              onLogCall={() => logCall(b.id)}
              onFailDelivery={() => failDelivery(b.id)}
              onReportProblem={() => setProblemFor(b)}
              onRecordKm={() => setKmFor({ booking: b, mode: isInTransit ? "delivery" : "pickup" })}
            />
          );
        })}
      </main>

      {/* === Floating SCAN QR button === */}
      {hasInProgress && (
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="fixed bottom-5 right-5 z-20 w-16 h-16 bg-brand-red hover:scale-105 text-white flex items-center justify-center shadow-2xl transition-transform"
          data-testid="driver-fab-scan"
          aria-label="Scan kundens QR-kode"
        >
          <ScanLine size={26} />
        </button>
      )}

      {/* Settings drawer */}
      {settingsOpen && (
        <SettingsDrawer
          onClose={() => setSettingsOpen(false)}
          onLogout={logout}
        />
      )}

      <PwaInstallPrompt />
      {deliverFor && (
        <DeliverProofModal
          booking={deliverFor}
          onClose={() => setDeliverFor(null)}
          onDelivered={() => { setDeliverFor(null); load(); }}
        />
      )}
      <DriverQRScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanned={() => load()}
      />
      {/* Fase 1C — Modals */}
      {problemFor && (
        <DriverProblemReportModal
          booking={problemFor === true ? null : problemFor}
          onClose={() => setProblemFor(null)}
          onSubmitted={() => { loadDriftStatus(); }}
        />
      )}
      {chatOpen && (
        <DriverChatModal onClose={() => { setChatOpen(false); loadDriftStatus(); }} />
      )}
      {kmFor && (
        <DriverKmModal
          booking={kmFor.booking}
          mode={kmFor.mode}
          onClose={() => setKmFor(null)}
          onSaved={() => { setKmFor(null); load(); }}
        />
      )}
      {activeTraining && (
        <TrainingModal
          training={activeTraining}
          canClose={pendingTrainings.length === 0}
          onClose={() => setActiveTraining(null)}
          onCompleted={() => { setActiveTraining(null); loadTrainings(); load(); }}
        />
      )}
      {pendingTrainings.length > 0 && !activeTraining && (
        <div className="fixed bottom-4 left-4 right-4 z-30 max-w-2xl mx-auto" data-testid="driver-training-banner">
          <button
            onClick={() => setActiveTraining(pendingTrainings[0])}
            className="w-full bg-rose-600 text-white py-3 px-4 font-bold uppercase tracking-wider text-sm shadow-lg flex items-center justify-between"
          >
            <span>⚠ {pendingTrainings.length} obligatorisk træning afventer</span>
            <span>ÅBN →</span>
          </button>
        </div>
      )}
    </div>
  );
}

// === TRAINING MODAL === //
function TrainingModal({ training, canClose, onClose, onCompleted }) {
  const [step, setStep] = React.useState("read"); // read | quiz | done
  const [answers, setAnswers] = React.useState({});
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const quiz = training.quiz || [];
      const quiz_answers = quiz.map((_, i) => answers[i] ?? -1);
      const { data } = await driverApi().post("/driver/trainings/complete", {
        training_id: training.id,
        quiz_answers,
      });
      setResult(data);
      setStep("done");
      if (data.passed) {
        setTimeout(() => onCompleted(), 2500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    } finally { setBusy(false); }
  };

  const quiz = training.quiz || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center" data-testid="training-modal">
      <div className="bg-white w-full sm:max-w-2xl sm:max-h-[90vh] overflow-y-auto">
        <div className="bg-rose-600 text-white p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-80">Obligatorisk træning</div>
            <div className="font-display font-bold text-lg">{training.title}</div>
          </div>
          {canClose && step === "done" && result?.passed && (
            <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="training-close">✕</button>
          )}
        </div>
        <div className="p-5 space-y-4">
          {step === "read" && (
            <>
              <p className="text-sm text-brand-text2 italic">Læs hele teksten igennem og tag derefter en kort quiz. Du skal score mindst 80% for at gennemføre.</p>
              <div className="text-brand-dark whitespace-pre-line leading-relaxed">{training.body}</div>
              <button onClick={() => setStep("quiz")} className="btn-primary w-full" data-testid="training-start-quiz">
                Gå til quiz ({quiz.length} spørgsmål)
              </button>
            </>
          )}
          {step === "quiz" && (
            <div className="space-y-5">
              {quiz.map((q, qi) => (
                <div key={qi} className="border-l-4 border-brand-red pl-4">
                  <div className="font-bold text-brand-dark mb-2">{qi + 1}. {q.q}</div>
                  <div className="space-y-1">
                    {q.options.map((opt, oi) => (
                      <label key={oi} className={`flex items-start gap-2 p-2 cursor-pointer hover:bg-slate-50 border ${answers[qi] === oi ? "border-brand-red bg-rose-50" : "border-transparent"}`}>
                        <input
                          type="radio"
                          name={`q${qi}`}
                          checked={answers[qi] === oi}
                          onChange={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                          data-testid={`training-q${qi}-o${oi}`}
                          className="mt-1"
                        />
                        <span className="text-sm">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <button
                onClick={submit}
                disabled={busy || Object.keys(answers).length < quiz.length}
                className="btn-primary w-full"
                data-testid="training-submit-quiz"
              >
                {busy ? "Sender..." : `Aflever (${Object.keys(answers).length}/${quiz.length} besvaret)`}
              </button>
            </div>
          )}
          {step === "done" && result && (
            <div className="text-center py-6" data-testid="training-result">
              <div className={`text-6xl mb-3 ${result.passed ? "text-emerald-500" : "text-rose-500"}`}>
                {result.passed ? "✓" : "✗"}
              </div>
              <div className="font-display font-bold text-2xl text-brand-dark">
                {result.passed ? "Gennemført!" : "Ikke bestået"}
              </div>
              <div className="text-brand-text2 mt-2">
                Score: {result.score_pct}% ({result.correct}/{result.total} korrekt)
              </div>
              {!result.passed && (
                <button onClick={() => { setAnswers({}); setStep("read"); setResult(null); }} className="btn-primary mt-4">
                  Prøv igen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// === EMPTY STATE === //
function EmptyState({ tab }) {
  const copy = {
    in_progress: { title: "Ingen ordrer i gang", body: "Du modtager besked her — og som notifikation — så snart der kommer en ny ordre." },
    all: { title: "Ingen ordrer i dag", body: "Der er ingen leveringer planlagt for i dag." },
    completed: { title: "Endnu ingen leverede", body: "Når du markerer pakker som leveret, vises de her." },
  }[tab];
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] p-10 text-center" data-testid="driver-empty">
      <div className="w-14 h-14 bg-white/[0.06] flex items-center justify-center mx-auto">
        <Package size={26} className="text-white/40" />
      </div>
      <h2 className="font-display font-bold text-xl text-white mt-5">{copy.title}</h2>
      <p className="text-white/50 text-sm mt-2 max-w-xs mx-auto leading-relaxed">{copy.body}</p>
    </div>
  );
}

// === STOP CARD (Burd-style) === //
function StopCard({ stopNumber, booking: b, isActive, isInTransit, isActionable, isUpdating, onScanQR, onMarkPickedUp, onCompleteDelivery, onLogCall, onFailDelivery, onReportProblem, onRecordKm }) {
  // For confirmed: show pickup info. For in_transit: show delivery info.
  const isPickup = b.booking_status === "confirmed";
  const target = isPickup
    ? { label: "Hent hos", name: b.customer_name, phone: b.customer_phone, addr: b.pickup_address, zip: b.pickup_zip, city: b.pickup_city }
    : { label: "Levér til", name: b.recipient_name || b.customer_name, phone: b.recipient_phone || b.customer_phone, addr: b.delivery_address, zip: b.delivery_zip, city: b.delivery_city };
  const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${target.addr}, ${target.zip} ${target.city}`)}`;
  const smsUrl = `sms:${target.phone?.replace(/\s+/g, "")}`;
  const fullAddr = `${target.addr}, ${target.zip} ${target.city}`;

  // Sender info ("Pakke fra") — shown when this is a delivery stop
  const pakkeFra = isInTransit ? `${b.pickup_city || b.pickup_address}` : DELIVERY_LABEL[b.delivery_type] || "Kurér";
  const status = b.booking_status;

  return (
    <article
      className={`bg-white/[0.03] border ${isActive ? "border-white/[0.12]" : "border-white/[0.06] opacity-60"} relative`}
      data-testid={`stop-${stopNumber}`}
    >
      {/* Stop number + icon-row */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
        <h2 className={`font-display font-bold text-3xl tracking-tight ${isActionable ? "text-white" : "text-white/40"}`}>
          {stopNumber}. Stop
        </h2>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={`tel:${target.phone}`}
            className={`w-12 h-12 border-2 ${isActionable ? "border-brand-red text-brand-red hover:bg-brand-red hover:text-white" : "border-white/15 text-white/40"} flex items-center justify-center transition-colors`}
            aria-label="Ring"
            data-testid={`stop-${stopNumber}-call`}
          >
            <Phone size={18} />
          </a>
          <a
            href={smsUrl}
            className={`w-12 h-12 border-2 ${isActionable ? "border-brand-red text-brand-red hover:bg-brand-red hover:text-white" : "border-white/15 text-white/40"} flex items-center justify-center transition-colors`}
            aria-label="Send SMS"
            data-testid={`stop-${stopNumber}-sms`}
          >
            <MessageSquare size={18} />
          </a>
          <a
            href={navUrl}
            target="_blank"
            rel="noreferrer"
            className={`w-12 h-12 ${isActionable ? "bg-brand-red text-white hover:scale-105" : "bg-white/10 text-white/40"} flex items-center justify-center transition-transform`}
            aria-label="Naviger"
            data-testid={`stop-${stopNumber}-nav`}
          >
            <Navigation size={18} />
          </a>
        </div>
      </div>

      {/* Field rows */}
      <div className="px-5 pb-5 space-y-4">
        <Field label="Navn" value={target.name} muted={!isActionable} />
        <Field label="Adresse" value={fullAddr} muted={!isActionable} />
        <Field label={isInTransit ? "Pakke fra" : "Service"} value={pakkeFra} muted={!isActionable} />
        {b.pickup_time && (
          <Field
            label={isInTransit ? "Forventet" : "Afhent kl."}
            value={b.pickup_time}
            icon={Clock}
            muted={!isActionable}
          />
        )}
        {b.delivery_instruction && isInTransit && (
          <Field label="Fuldmagt" value={b.delivery_instruction} icon={Key} muted={!isActionable} />
        )}
        {b.notes && (
          <Field label="Note" value={b.notes} muted={!isActionable} />
        )}
      </div>

      {/* Primary action button */}
      {isActive && (
        <div className="px-5 pb-5">
          {isPickup ? (
            <button
              type="button"
              onClick={isActionable ? onScanQR : null}
              disabled={!isActionable || isUpdating}
              data-testid={`stop-${stopNumber}-action`}
              className={`w-full py-4 font-display font-bold text-base uppercase tracking-[0.15em] transition-all ${
                isActionable
                  ? "bg-brand-red text-white hover:opacity-90 active:scale-[0.99]"
                  : "bg-white/[0.08] text-white/30 cursor-not-allowed"
              }`}
            >
              {isUpdating ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Afhentning"}
            </button>
          ) : (
            <button
              type="button"
              onClick={isActionable ? onCompleteDelivery : null}
              disabled={!isActionable || isUpdating}
              data-testid={`stop-${stopNumber}-action`}
              className={`w-full py-4 font-display font-bold text-base uppercase tracking-[0.15em] transition-all ${
                isActionable
                  ? "bg-brand-red text-white hover:opacity-90 active:scale-[0.99]"
                  : "bg-white/[0.08] text-white/30 cursor-not-allowed"
              }`}
            >
              {isUpdating ? <Loader2 size={18} className="animate-spin mx-auto" /> : "Levering"}
            </button>
          )}
          {isPickup && isActionable && (
            <button
              type="button"
              onClick={onMarkPickedUp}
              data-testid={`stop-${stopNumber}-mark-pickup`}
              className="w-full mt-2 py-2.5 text-[11px] tracking-wider uppercase font-bold text-white/50 hover:text-white border border-white/10 hover:border-white/20"
            >
              Manuel: markér afhentet uden QR
            </button>
          )}
          {!isPickup && isActionable && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                onClick={onLogCall}
                data-testid={`stop-${stopNumber}-log-call`}
                className="py-2.5 text-[11px] tracking-wider uppercase font-bold text-white/70 hover:text-white border border-white/10 hover:border-white/20 inline-flex items-center justify-center gap-1"
                title="Registrér at du har ringet til kunden"
              >
                <Phone size={12} /> Log opkald
              </button>
              <button
                type="button"
                onClick={onFailDelivery}
                data-testid={`stop-${stopNumber}-fail`}
                className="py-2.5 text-[11px] tracking-wider uppercase font-bold text-rose-300 hover:text-rose-200 border border-rose-300/30 hover:border-rose-300/60"
                title="Markér som forgæves (kræver opkald først for 'ikke hjemme')"
              >
                Forgæves
              </button>
            </div>
          )}
          {/* Fase 1C — Km + Problem buttons */}
          {isActive && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                onClick={onRecordKm}
                data-testid={`stop-${stopNumber}-km`}
                className="py-2.5 text-[11px] tracking-wider uppercase font-bold text-white/70 hover:text-white border border-white/10 hover:border-white/20 inline-flex items-center justify-center gap-1"
                title="Registrér kilometer for denne tur"
              >
                <Gauge size={12} />
                {b.actual_km != null ? `${b.actual_km} km` : (b.pickup_km != null ? "Slut-km" : "Start-km")}
              </button>
              <button
                type="button"
                onClick={onReportProblem}
                data-testid={`stop-${stopNumber}-problem`}
                className="py-2.5 text-[11px] tracking-wider uppercase font-bold text-amber-300 hover:text-amber-200 border border-amber-300/30 hover:border-amber-300/60 inline-flex items-center justify-center gap-1"
                title="Rapportér problem"
              >
                <AlertTriangle size={12} /> Problem
              </button>
            </div>
          )}
        </div>
      )}

      {status === "delivered" && (
        <div className="px-5 pb-5">
          <div className="bg-emerald-900/30 border border-emerald-700/40 px-4 py-3 flex items-center gap-2 text-emerald-300 text-sm">
            <CheckCircle2 size={16} /> <span className="font-semibold">Leveret</span>
            {b.delivered_at && <span className="text-emerald-400/70 text-xs ml-auto">{new Date(b.delivered_at).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })}</span>}
          </div>
        </div>
      )}
    </article>
  );
}

// === FIELD ROW (label / value) === //
function Field({ label, value, icon: Icon, muted }) {
  return (
    <div>
      <div className={`text-[11px] tracking-[0.18em] uppercase ${muted ? "text-white/30" : "text-white/40"}`}>{label}</div>
      <div className={`flex items-center gap-2 mt-0.5 ${muted ? "text-white/40" : "text-white"}`}>
        {Icon && <Icon size={14} className="flex-shrink-0 opacity-70" />}
        <span className="font-display font-semibold text-base leading-tight break-words">{value || "—"}</span>
      </div>
    </div>
  );
}

// === SETTINGS DRAWER === //
function SettingsDrawer({ onClose, onLogout }) {
  React.useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onEsc); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center sm:justify-end" data-testid="driver-settings-drawer">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#0A0A0A] border-l border-white/[0.08] w-full sm:max-w-sm sm:h-screen overflow-y-auto shadow-2xl text-white">
        <div className="sticky top-0 bg-[#0A0A0A] px-5 py-4 flex items-center justify-between border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Settings size={17} />
            <div className="font-display font-semibold">Indstillinger</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center hover:bg-white/5" data-testid="settings-close">
            <X size={17} />
          </button>
        </div>
        <div className="p-5 space-y-6">
          <section>
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/40 mb-2">Notifikationer</h3>
            <DriverPushToggle />
          </section>
          <section>
            <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/40 mb-2">Konto</h3>
            <button
              onClick={onLogout}
              data-testid="settings-logout"
              className="w-full border border-white/15 text-white px-4 py-3 font-semibold text-sm uppercase tracking-wider hover:bg-white hover:text-[#0A0A0A] transition-colors flex items-center justify-center gap-2"
            >
              <LogOut size={15} /> Log ud
            </button>
          </section>
          <section className="pt-4 border-t border-white/[0.06] text-center">
            <div className="text-[10px] uppercase tracking-widest text-white/40">EMA Kurer ApS</div>
            <div className="text-xs text-white/40 mt-1">Direkte kurérservice · A → B</div>
            <div className="text-[10px] text-white/30 mt-3">GPS-sporing er aktiv som en del af tjenesten</div>
          </section>
        </div>
      </div>
    </div>
  );
}
